import React, { useEffect, useState, useLayoutEffect, useRef } from 'react';
import "mathlive";
import { MathfieldElement } from "mathlive";
import Backdrop from 'components/Backdrop';
import Button from 'components/Button';
import TextInput from 'components/TextInput2';
import Loader from 'components/Loader';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import { IoClose } from "react-icons/io5";
import DragAndDropBlankPreview from 'components/QuestionPopup/DragAndDropBlankPreview';
import DragAndDropBucketPreview from 'components/QuestionPopup/DragAndDropBucketPreview';
import TableGridPreview from 'components/QuestionPopup/TableGridPreview';
import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

import './index.css';

type questionPopupProps = {
    info: {
        testType: string;
        moduleName: string;
        subject: string;
        category: string;
        questionDifficulty: string;
        questionType: number;
        questionNumber: number;
    };
    correctStudent: string;
    testName: string;
    question: string;
    passages: {
        id: string;
        introduction: string;
        passage: string;
        passageType: string;
        order: number;
        indices: {
            startIndex: number;
            endIndex: number;
        }[];
        passageFigures: {
            figure: {
                id: string;
                image: string | null;
            }
            index: number;
        } [];
    }[];
    figures: {
        id: string;
        image: string | null;
        order: number;
    }[];
    choices?: {
        id: number;
        choice: string;
        figure: string | null;
        isCorrect: boolean;
    }[];
    hotText?: {
        hotTextId: number;
        index: number;
        answer: string;
        isCorrect: boolean;
    }[];
    buckets?: {
        bucketId: string;
        index: number;
        name?: string;
    }[];
    dragAndDropItems?: {
        id: string;
        index: number;
        answer: string;
    }[];
    tableGridRows?: {
        id: string;
        index: number;
        name: string;
    }[];
    tableGridColumns?: {
        id: string;
        index: number;
        name: string;
    }[];
    inlineChoices?: {
        index: number;
        answer: string;
    }[];
    questionHelper?: {
        id: string;
        questionHelper: string;
    };
    correctChoice?: number | null; 
    correctAnswers?: string | [number, string][] | string [];
    manageExit?: (event: any) => void;
    innerRef?: React.RefObject<HTMLDivElement>;
}

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const DIFFICULTY_THRESHOLDS = {
  HARD: 40,
  MEDIUM: 80,
};

function questionPopup({
    info,
    correctStudent,
    testName,
    question,
    passages,
    figures,
    choices,
    hotText,
    buckets,
    dragAndDropItems,
    tableGridRows,
    tableGridColumns,
    inlineChoices,
    questionHelper,
    correctChoice,
    correctAnswers,
    manageExit,
    innerRef
}: questionPopupProps) {
    const [showAnswer, setShowAnswer] = useState(false);
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [highlighting, setHighlighting] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [questionHelp, setQuestionHelp] = useState(null);
    const [questionHelpFB, setQuestionHelpFB] = useState(null);
    const [icQuestionHelper, setIcQuestionHelper] = useState(null);
    const passageContainerRef = useRef(null);
    const mathfieldRef = useRef<MathfieldElement | null>(null);
    
    useEffect(() => {
        const keyboard = (window as any).mathVirtualKeyboard;
        // keyboard.visible = false;
        // keyboard.hide()

        // always visible
        if (info?.questionType === 11 || info?.questionType === 12) {
            mathfieldRef.current.mathVirtualKeyboardPolicy = 'manual'
        } else {
            if (mathfieldRef.current) mathfieldRef.current.mathVirtualKeyboardPolicy = 'auto';
            keyboard.hide();
        }
        
        const mf = mathfieldRef.current;
        if (mf) mf.setAttribute("virtual-keyboard-toolbar", "none");

        if (!keyboard || !mf) return;
        
        if (info.questionType !== 11 && info.questionType !== 12) {
            keyboard.visible = false;
            return;
        }
        
        // keyboard customization
        if (info.questionType === 11) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","x","y",""],
                    ["6","7","8","9","0","+","−","\\sqrt{#0}"],
                    ["\\%","−",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","\\cdot","\\div","\\sqrt[#0]{#0}"],
                    ["","{#0}^{#0}","({#0})","\\le","\\lt","=","\\ge","\\gt"],
                    ["\\pi","","","","","","","\\lvert{#@}\\rvert"]
                ]
            };
            keyboard.show();
            keyboard.visible = true;
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".questionPopup__eeContainer .MLK__keycap");
                let minusCount = 0; 

                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "x": tooltip = "letter x"; break;
                        case "y": tooltip = "letter y"; break;
                        case "+": tooltip = "add"; break;
                        case "−":
                        case "-":
                            minusCount++;
                            tooltip = minusCount === 1 ? "subtract" : "negative sign";
                            break;
                        case "÷": tooltip = "divide"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "=": tooltip = "equal"; break;
                        case "≤": tooltip = "less than or equal"; break;
                        case "<": tooltip = "less than"; break;
                        case "≥": tooltip = "greater than or equal"; break;
                        case ">": tooltip = "greater than"; break;
                        case "π": tooltip = "constant pi"; break;
                        case "⋅": tooltip = "multiply dot"; break;
                        case "√▢​": tooltip = "square root"; break;
                        case "▢√▢​": tooltip = "general root"; break;
                        case "▢▢": tooltip = "exponent"; break;
                        case "(▢)": tooltip = "parentheses"; break;
                        case "∣■∣": tooltip = "absolute value"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);

        } else if (info.questionType === 12) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","","",""],
                    ["6","7","8","9","0","","",""],
                    ["\\%","-",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","","",""]
                ]
            }; 
            keyboard.show();
            keyboard.visible = true;   
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".questionPopup__eeContainer .MLK__keycap");
                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "−": tooltip = "negative sign"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);        
        }

        // remove toggle menu buttons, keyboard location, keyboard styling
        const style = document.createElement("style");
        style.innerHTML = `
            math-field::part(menu-toggle),
            math-field::part(virtual-keyboard-toggle) { display: none !important; }

            .questionPopup__eeContainer { position: relative; }
            .questionPopup__eeContainer .ML__virtual-keyboard {
                position: absolute !important;
                top: calc(100% + 8px) !important;
                left: 0 !important;
                width: 620px !important;
                z-index: 999 !important;
            }
            .questionPopup__eeContainer .MLK__backdrop {
                background: none;
            }
            .questionPopup__eeContainer .MLK__toolbar { display: none; }
            .questionPopup__eeContainer .MLK__keycap {
                border: 1px solid #676767;
                flex: 1;
                cursor: none;
                pointer-events: none;
            }
            .questionPopup__eeContainer .ML__keyboard [data-tooltip] {
                position: relative;
                pointer-events: none;
            }
            .ML__latex .ML__vlist-t2,
            .ML__delim-size1 {
                font-size: 16px;
            }
            .questionPopup__eeContainer .MLK__toolbar { 
                justify-content: flex-start; 
            }
            .questionPopup__eeContainer .MLK__plate {
                border: 1px solid #bdbdbd;
                padding: 4px;
            }
            .questionPopup__eeContainer :where(.MLK__rows > .MLK__row div) {
                border: 1px solid #bdbdbd;
                border-radius: 1px;
                font-size: 18px;
                background: white;
            }
            .questionPopup__eeContainer .MLK__rows > .MLK__row div:empty {
                pointer-events: none;
                opacity: 0.5;         
                background-color: white;
                border: 1px solid #e0e0e0ff;
            }
            .questionPopup__eeContainer .MLK__rows {
                gap: 5px;
            }    
            .questionPopup__eeContainer .MLK__rows > .MLK__row {
                gap: 5px;
                justify-content: flex-start;
            }    
            .questionPopup__eeContainer .MLK__row:nth-child(-n+2) div:nth-child(-n+5) {
                background-color: #eeeeeec7 !important;
            }
        `;
        document.head.appendChild(style);

        // keyboard location
        const container = document.querySelector(".questionPopup__eeContainer");
        let observer: MutationObserver | null = null;
        if (container) {
            keyboard.container = container;
            keyboard.alwaysVisible = true;
            keyboard.floater = false;
            keyboard.autoDismiss = false;
            keyboard.show();
        }
        setTimeout(() => {
            const vk = container?.querySelector(".ML__keyboard") as HTMLElement;
            if (vk) {
                // keyboard location styling
                vk.style.height = "260px";
                vk.style.pointerEvents = "auto";
                vk.style.opacity = "1";
                vk.style.visibility = "visible";
                vk.style.removeProperty('--_keyboard-height'); 
                vk.style.height = '300px';    

                // always keyboard visible
                observer = new MutationObserver(() => {
                    if (
                        vk.style.display === "none" ||
                        vk.style.visibility === "hidden" ||
                        vk.style.opacity === "0"
                    ) {
                        vk.style.display = "block";
                        vk.style.visibility = "visible";
                        vk.style.opacity = "1";
                        vk.style.pointerEvents = "auto";
                    }
                });
                observer.observe(vk, {
                    attributes: true,
                    attributeFilter: ["style"],
                    subtree: false,
                });
            }
            if (mf) {
                // always initially
                mf.setAttribute("readonly", "true"); // 입력 불가
                mf.style.caretColor = "transparent";  // 커서 숨기기
                mf.style.userSelect = "none";         // 텍스트 선택 불가
                mf.style.pointerEvents = "none";      // 클릭 이벤트 차단
                // input focus style
                mf.addEventListener("focusin", () => { mf.style.border = "2px solid #bdbdbd";});
                mf.addEventListener("focusout", () => { mf.style.border = "1px solid #bdbdbd";});
                
                // always keyboard visible
                const preventHide = (e: FocusEvent) => {
                    setTimeout(() => {
                        if (mf && document.activeElement !== mf) { // refocus
                            mf.focus();
                        }
                        keyboard.show();
                        if (vk) {
                            vk.style.display = "block";
                            vk.style.visibility = "visible";
                            vk.style.opacity = "1";
                            vk.style.pointerEvents = "auto";
                        }
                    }, 10); // delay after hiding keyboard
                };
                mf.addEventListener("blur", preventHide, true);
                mf.addEventListener("focusout", preventHide, true);
            }
        }, 50);
        return () => {
            // style 제거
            document.head.removeChild(style);
            // observer 해제
            if (observer) observer.disconnect();
        };
    }, [info]);

    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;
        if (info.questionType === 11 || info.questionType === 12){
            const correctAnswersArray = correctAnswers as string[];
            let correctAnswer = correctAnswersArray.map(ans => ans.replace(/\\\\/g, '\\')).join(', ');

            if (showAnswer) {
                mf.setValue(" \\; \\color{green}{" + correctAnswer + "}");
            } else {
                mf.setValue("");
            }
        }
    }, [showAnswer, info]);

    const generateHightlightIndexing = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }

            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                passageHTML += `
                    <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                `;                    
            }

            setHighlighting(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }

    };

    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        passageHTML += `
                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                        `;                    
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            passageHTML += `
                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
            `;                    
        }
        
        setFullPassage({
            html: passageHTML
        });

    };

    // Question Helper
    const generateQuestionHelper = ({ questionHelper, hotText = [], correctAnswers = [] }) => {
        if (!questionHelper) return '';

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        // 매칭되는 부분을 span으로 감싸기

        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const className = showAnswer && correctAnswers.includes(index)? " correct": "";
            return `<div class="questionPopup__questionHelperHotText${className}">${match}</div>`;
        });
        setQuestionHelp(html);
    };

    // Fill in the Blank Question Helper
    const generateFBQuestionHelper = ({ questionHelper, correctAnswers = []}) => {
        if (!questionHelper) return '';
        const groupedCorrects = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrects[idx]) groupedCorrects[idx] = [];
            groupedCorrects[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const correctVals = groupedCorrects[index] || []; 
            const correctAnswer = correctVals.length > 0 ? correctVals.join(", ") : "";
            // const value = showAnswer? 
            const width = Math.max(50, correctVals.reduce((acc, ans) => Math.max(acc, ans?.length || 0), 0) * 20 + 30)
            if (showAnswer) {
                return `
                    <span class="questionPopup__questionHelperFBBox" style="display:inline-block; width:${width}px">
                        <span class="questionPopup__fbCorrectAnswer">${correctAnswer}</span>
                    </span>
                `;
            } else {
                return `<input class="questionPopup__questionHelperFB" style="width:${width}px" data-index="${index}" value="" />`;
            }        
        });
        setQuestionHelpFB(html);
    };

    // Inline Choice Question Helper
    const generateICQuestionHelper = ({ questionHelper, inlineChoices = [], correctAnswers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedCorrect = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrect[idx]) groupedCorrect[idx] = [];
            groupedCorrect[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;

            // index별 inlineChoices 찾아오기
            const choices = inlineChoices.filter(choice => choice.index === index);
            const width = Math.max(70, (choices.length > 0 ? Math.max(...choices.map(c => c.answer?.length || 0)) : 0) * 20);
            const optionsHtml = choices
                .map(
                    (choice) => {
                        const correctAnswer = groupedCorrect[index]?.includes(choice.answer);
                        let className = ''
                        if (showAnswer && correctAnswer) {
                            className = "questionPopup__fbCorrectAnswer";
                        } 
                        return `<span class="${className}" value="${choice.answer}">${choice.answer}</span>`
                })
                .join(", ");

            return `<span class="questionPopup__questionHelperIC" style="width:${width}px" data-index="${index}">
                        ${optionsHtml}
                    </span>`;
        });
        setIcQuestionHelper(html);
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }   

        // This one DOES name SHSAT's subject ('English Language Arts'), so the
        // poem passage reaches it — open a Class SHSAT Report and click an ELA
        // question. Optional-chained because `enabled` is evaluated eagerly now,
        // unlike the old in-loop test.
        applyKatexWrap(document, {
            enabled: info?.['subject'] === 'Reading and Writing'
                || info?.['subject'] === 'ACT English'
                || info?.['subject'] === 'ACT Reading'
                || info?.['subject'] === 'ACT Science'
                || info?.['subject'] === 'English Language Arts',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useLayoutEffect(() => {
        if (highlighting.length > 0) {
            const passageId = highlighting[0].passageId;
            const passage = document.getElementById(`highlighting ${passageId}`);
            // highlighting 여러개일 경우 첫 번째 
            if (passage) {
                let firstHighlight = passage.querySelector("mark"); // 첫 번째 mark 태그 찾기
                let offset = 0;
                while (firstHighlight !== passage) {
                    offset = firstHighlight.offsetTop;
                    firstHighlight = firstHighlight.parentElement;
                }
                if (firstHighlight && passageContainerRef.current) {
                    passageContainerRef.current.scrollTo({
                        top: firstHighlight? offset - passageContainerRef.current.offsetTop: 0,
                        behavior: "smooth", 
                    });
                }
            }
        }
    }, [highlighting]);

    useLayoutEffect(() => {
        if (passages?.length > 0) {
            renderKatex();
            setHighlighting([]);
            setFullPassage(null);
            const passageData = passages;
            if (passageData) {
                const { id, indices, passageFigures } = passages[0]
                if (info['testType']==='Digital ACT') {
                    if (indices) generateHightlightIndexing({ id, indices, passageFigures });  
                }
                getPassageWithFigure({ id, passageFigures });
            }
        }
    }, [passages]);

    useEffect(()=>{
        renderKatex();
        // question helper
        if (questionHelper) {
            if (info['questionType'] === 4) {
                generateQuestionHelper({ 
                    questionHelper: questionHelper, 
                    hotText: hotText || [],
                    correctAnswers: hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || []                
                });
            } else if (info['questionType'] === 7) {
                generateFBQuestionHelper({ 
                    questionHelper: questionHelper, 
                    correctAnswers: Array.isArray(correctAnswers) ? correctAnswers : []   
                });
            } else if (info['questionType'] === 9) {
                generateICQuestionHelper({ 
                    questionHelper: questionHelper, 
                    inlineChoices: inlineChoices,
                    correctAnswers: Array.isArray(correctAnswers) ? correctAnswers : []   
                });
            }
        } 
    }, [questionHelper, questionHelpFB, icQuestionHelper, showAnswer])

    useEffect(()=>{
        renderKatex();
    }, [question])

    if (info === null || info === undefined) return <Loader />;

    return (
        <>
            <Backdrop />
            <div className='questionPopup' ref={innerRef}>
                {/* header */}
                <div className='questionPopup__header'>
                    <div className='questionPopup__titleGroup'>
                        <h2 className='questionPopup__title'>
                            {testName}
                        </h2>
                        <div className='questionPopup__moduleName'>{(info['testType']==='Digital SAT'||info['testType']==='Digital PSAT'||info['testType']==='TSIA2'||info['testType']==='BCA')? info['moduleName']: info['subject'].replace('ACT ', '').replace('Mathematics', 'math')}</div>
                    </div>
                    <IoClose className='questionPopup__closeIcon' onClick={()=>{manageExit(false)}} />
                </div>
                <div className='questionPopup__dottedLine'></div>

                {/* body */}
                <div className='questionPopup__test'>
                    {passages?.length > 0 && 
                        <>
                            <div
                                id='questionPopup__material'
                                className='questionPopup__material'
                                ref = {passageContainerRef}
                            >
                                <div className='questionPopup__materialContent'>
                                    {(info['testType']==='Digital SAT' || info['testType']==='Digital PSAT') ? 
                                        <div className='questionPopup__passages' id='questionPopup__passages'>
                                            {figures?.map(figure => (
                                                <img
                                                    className='questionPopup__figure'
                                                    key={figure.id}
                                                    src={figure.image}
                                                    // alt={(questionIndex + 1).toString()}
                                                    draggable={false}
                                                />
                                            ))}
                                            {passages?.map(({ id, passage }) => (
                                                <div key={id} className='questionPopup__passage'>
                                                    <p
                                                        id={id}
                                                        className='katex questionPopup__passageText'
                                                    >
                                                        {passage}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                        : 
                                        <div className='questionPopup__passages'>
                                            <div key={passages[0].id} className='questionPopupACT__passage'>
                                                {/* 해당 id를 가진 passage의 p태그 가져와서 highlight 넣은 html 받아와서 겹쳐 보여줌 (z-index, transparent 조절) */}
                                                <p
                                                    id={passages[0].id}
                                                    className='katex questionPopupACT__passageText'
                                                >
                                                    {passages[0].passage}
                                                </p>
                                                <p
                                                    id={`passage ${passages[0].id}`}
                                                    className='katex questionPopupACT__passageTextPassage'
                                                    dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                                >
                                                </p>
                                                {/* {questionNumber > 0 &&  */}
                                                {highlighting.length > 0 &&
                                                    highlighting.map((highlight, index)=>
                                                        <div
                                                            id={`highlighting ${passages[0].id}`}
                                                            key={index}
                                                            className='katex questionPopupACT__Highlight'
                                                            dangerouslySetInnerHTML={{ __html: highlight?.html }}
                                                        ></div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className='questionPopup__verticalLine'></div>
                        </>
                    }

                    <div className='questionPopup__question' id='questionPopup__question'>
                        <div className='questionPopup__questionContent'>
                            <div className='questionPopup__questionHeader'>
                                <p className='questionPopup__questionNumber'>
                                    {info['questionNumber']}
                                </p>
                                <button
                                    className={showAnswer
                                        ? 'questionPopup__showAnswer--active'
                                        : 'questionPopup__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>
                            <div className='questionPopup__questionChoices' id='questionPopup__questionChoices'>
                                {(info['subject'] === 'Math' || info['subject'] === 'ACT Mathematics' || info['subject'] === 'Mathematics') && figures?.map(figure => (
                                    <img
                                        className='questionPopup__figure'
                                        key={figure.id}
                                        src={figure.image}
                                        // alt={(questionIndex + 1).toString()}
                                        draggable={false}
                                    />
                                ))}
                                <p className='questionPopup__questionQuestion'>
                                    <span className='katex'>
                                        {question}
                                    </span>
                                </p>
                                {(info['testType']==='Digital SAT' || info['testType']==='Digital PSAT' || info['testType']==='Digital ACT' || info['testType']==='TSIA2' || info['testType']==='BCA') || (info['testType']==='Digital SHSAT' && (info.questionType === 1 || info.questionType === 3))
                                ? (choices?.length > 0
                                    ?   <>
                                            {choices?.map((choice, i) => (
                                                <div key={choice.id} className='questionPopup__questionChoiceWrapper'>
                                                    <button
                                                        className={showAnswer && choice.isCorrect
                                                            ? 'questionPopup__questionChoice--correct'
                                                            : 'questionPopup__questionChoice'
                                                        }
                                                    >
                                                        <div className='questionPopup__questionChoiceOutline'></div>
                                                        <p className='questionPopup__questionChoiceLetter'>
                                                            {MULTIPLE_CHOICES[i]}
                                                        </p>
                                                        {choice.figure
                                                        ?   <img
                                                                className='questionPopup__questionChoiceFigure'
                                                                key={choice.figure}
                                                                src={choice.figure}
                                                                alt={choice.figure}
                                                                draggable={false}
                                                            />
                                                        :   <p className='questionPopup__questionChoiceText'>
                                                                <span className='katex'>
                                                                    {choice.choice}
                                                                </span>
                                                            </p>
                                                        }
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    :   <div className='questionPopup__questionAnswerReview'>
                                            <TextInput
                                                className='questionPopup__questionAnswer'
                                                inputClassName={showAnswer? 'questionPopup__questionCorrectAnswer': 'questionPopup__questionAnswerInput'}
                                                // name={name + q}
                                                value={correctAnswers
                                                    ? (showAnswer
                                                        ? correctAnswers.toString().replaceAll(',', ', ')
                                                        : ''
                                                        )
                                                    : ''
                                                }
                                                onChange={() => {}}
                                            />
                                        </div>
                                    )
                                : info.questionType === 4
                                ? <>
                                    {hotText?.length > 0 && 
                                        <div id='questionPopup__questionHelper' className='questionPopup__questionChoiceWrapper'>
                                            <p
                                                className='katex questionPopup__questionHelperText'
                                                dangerouslySetInnerHTML={{ __html: questionHelp }}
                                            ></p>
                                        </div>
                                    }
                                </>
                                : info.questionType === 5
                                ? <DragAndDropBucketPreview
                                    question={{
                                        answers: correctAnswers,
                                        buckets: buckets,
                                        dragAndDropItems: dragAndDropItems
                                    }}
                                    showAnswer={showAnswer}
                                />
                                : info.questionType === 6
                                ? <DragAndDropBlankPreview
                                    question={{
                                        answers: correctAnswers,
                                        dragAndDropItems: dragAndDropItems,
                                        questionHelper: questionHelper
                                    }}
                                    showAnswer={showAnswer}
                                />
                                : info.questionType ===7
                                ? <div id='questionPopup__questionHelper' className='questionPopup__questionChoiceWrapper'>
                                    <p
                                        className='katex questionPopup__questionHelperText'
                                        dangerouslySetInnerHTML={{ __html: questionHelpFB }}
                                    ></p>
                                </div>
                                : info.questionType === 8
                                ? <TableGridPreview
                                    question={{
                                        answers: correctAnswers,
                                        tableGridRows: tableGridRows,
                                        tableGridColumns: tableGridColumns
                                    }}
                                    showAnswer={showAnswer}
                                />
                                : info.questionType === 9
                                ? <div className='questionPopup__questionChoiceWrapper'>
                                    <p
                                        className='katex questionPopup__questionHelperText'
                                        dangerouslySetInnerHTML={{ __html: icQuestionHelper }}
                                    ></p>
                                </div>
                                : info.questionType === 10
                                ? <div className='questionPopup__questionContainerHotSpot'>
                                    {choices?.length > 0 &&   
                                        <>
                                            {choices?.map((choice, i) => (
                                                <div 
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'questionPopup__choiceContainerHotSpot--correct'
                                                        : 'questionPopup__choiceContainerHotSpot'
                                                    }
                                                >
                                                    {choice.figure
                                                    ?   <img
                                                            className='questionPopup__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure} 
                                                            alt={choice.figure}
                                                        />
                                                    :   <p className='questionPopup__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </div>
                                            ))}
                                        </>
                                    }
                                </div>
                                : info.questionType===11 || info.questionType===12
                                ? <div className='questionPopup__questionContainer'>
                                    <div className='questionPopup__eeContainer'>
                                        <math-field
                                            ref={mathfieldRef}
                                            style={{
                                                width: "620px",
                                                height: "50px",
                                                fontSize: "20px"
                                            }}
                                            className='questionPopup__eeInput'
                                            virtual-keyboard-mode="manual"
                                        >
                                        </math-field>
                                    </div>
                                </div>
                                : <></>
                                }
                                <div className='questionPopup__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* footer */}
                <div className='questionPopup__dottedLine'></div>
                <div className='questionPopup__footer'>
                    {info['category'] &&
                        <div className='questionPopup__dataBox'>
                            <p className='questionPopup__dataTitle'>Category: </p>
                            <p className='questionPopup__category'>{info['category']}</p>
                        </div>
                    }
                    {correctStudent &&
                        <div className='questionPopup__dataBox'>
                            <div className='questionPopup__dataTitle'>Correct: </div>
                            <div className='questionPopup__correctStudent'>{correctStudent}</div>
                        </div>
                    }
                    {/* <div className='questionPopup__dataBox'>
                        <div className='questionPopup__dataTitle'>Difficulty: </div>
                        <div className={`questionPopup__difficulty
                            ${info['questionDifficulty'] == null || info['questionDifficulty'] === ''
                                ? 'questionPopup__noDifficulty'
                                : parseFloat(info['questionDifficulty']) <= DIFFICULTY_THRESHOLDS.HARD
                                ? 'questionPopup__hard'
                                : parseFloat(info['questionDifficulty']) <= DIFFICULTY_THRESHOLDS.MEDIUM
                                ? 'questionPopup__mdedium'
                                : 'questionPopup__easy'
                            }
                        `}>
                            {info['questionDifficulty'] == null || info['questionDifficulty'] === ''
                                ? '-'
                                : parseFloat(info['questionDifficulty']) <= DIFFICULTY_THRESHOLDS.HARD
                                ? 'Hard'
                                : parseFloat(info['questionDifficulty']) <= DIFFICULTY_THRESHOLDS.MEDIUM
                                ? 'Medium'
                                : 'Easy'
                            }
                        </div>
                    </div> */}
                </div>
            </div>
        </>
    );
}

export default questionPopup;